import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

/**
 * Thin wrapper around the `/sales-assistant/*` backend API (base `/api/v1`).
 *
 * Two audiences:
 *  - Assistant-facing (`assigned-agents`, `act-as`) — called with the assistant's own token.
 *  - Admin-facing links CRUD — called with a Master Admin / Operations Manager token.
 *
 * All calls go through ApiService, so they inherit the existing auth interceptor,
 * base URL and error handling. See SALES_ASSISTANT_FRONTEND.md §3 and §6.
 */
@Injectable({ providedIn: 'root' })
export class SalesAssistantService {
  constructor(private api: ApiService) {}

  // ---- Assistant-facing (assistant token) ---------------------------------

  /** Agents this assistant may act as. `data.can_access_other_users` flags whether the user is an assistant at all. */
  getAssignedAgents(): Observable<any> {
    return this.api.get('/sales-assistant/assigned-agents');
  }

  /**
   * Mint an impersonation token for `agentUserId`. The returned `data.access_token`
   * becomes the active bearer token; `data.acting_as` carries the agent's id/office.
   * MUST be called while the active token is the assistant's own token.
   */
  actAs(agentUserId: string): Observable<any> {
    return this.api.post('/sales-assistant/act-as', { agent_user_id: agentUserId });
  }

  // ---- Agent-facing approval (agent's own token) --------------------------

  /** Assistants linked to the logged-in agent, with each one's approval status and granted modules. */
  getMyAssistants(): Observable<any> {
    return this.api.get('/sales-assistant/my-assistants');
  }

  /** Approve/revoke an assistant and set which modules they may see while acting as this agent. */
  updateAssistantAccess(assistantId: string, payload: { status: 'approved' | 'revoked'; allowed_modules: string[] }): Observable<any> {
    return this.api.put('/sales-assistant/my-assistants/' + assistantId, payload);
  }

  /** Agent declines an assistant — unlinks the agent from that assistant so they drop off both sides' lists. */
  declineAssistant(assistantId: string): Observable<any> {
    return this.api.delete('/sales-assistant/my-assistants/' + assistantId);
  }

  // ---- Admin-facing links management (admin token) ------------------------

  /** Create/replace an assistant→agents link (upsert — one record per assistant). */
  upsertLink(payload: { assistant_user_id: string; agent_user_ids: string[]; office_id?: string }): Observable<any> {
    return this.api.post('/sales-assistant/links', payload);
  }

  /** List all links, optionally scoped to an office. */
  getLinks(officeId?: string): Observable<any> {
    const suffix = officeId ? `?office_id=${officeId}` : '';
    return this.api.get('/sales-assistant/links' + suffix);
  }

  /** Fetch a single assistant's link (404 when none exists yet). */
  getLink(assistantId: string): Observable<any> {
    return this.api.get('/sales-assistant/links/' + assistantId);
  }

  /** Remove an assistant's link entirely. */
  deleteLink(assistantId: string): Observable<any> {
    return this.api.delete('/sales-assistant/links/' + assistantId);
  }
}
